<script setup>
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import EditorjsList from '@editorjs/list';
import ImageTool from '@editorjs/image';
import PanelLayout from "@/layouts/PanelLayout.vue";

const route = useRoute();
const router = useRouter();

import {onMounted, ref} from 'vue';
import {Loader, Plus, Save, Copy} from "lucide-vue-next";
import {Input} from "@/components/ui/input/index.js";
import {Button} from "@/components/ui/button/index.js";
import {GetPanel, UpdatePanel} from "@/services/panelsService.js";
import {getContext} from "@/services/context.js";
import {RouterLink, useRoute, useRouter} from "vue-router";
import {Label} from "@/components/ui/label/index.js";
import {GetOrganisation} from "@/services/organisationService.js";
import {GetApp} from "@/services/appService.js";
import {Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator} from "@/components/ui/breadcrumb/index.js";
import {Separator} from "@/components/ui/separator/index.js";
import {cn} from "@/lib/utils.js";
import {UploadFile} from "@/services/uploadService.js";

const organisation = ref(null);
const app = ref(null);
const isLoading = ref(true);
const showChangeLogPanel = ref(false);

const panel = ref(null);
const token = ref(null);
const changeComment = ref(null);

let editor = null;

onMounted(async () => {
  let context = getContext(route);

  organisation.value = await GetOrganisation(getContext(route));
  app.value = await GetApp(context);
  panel.value = await GetPanel(context, route.params.panelId);
  token.value = panel.value.token;

  isLoading.value = false;

  editor = new EditorJS({
    holder: 'editorjs',
    placeholder: 'Let`s write an awesome story!',
    data: JSON.parse(panel.value.schema),

    tools: {
      header: Header,
      list: {
        class: EditorjsList,
        inlineToolbar: true,
        config: {
          defaultStyle: 'unordered'
        },
      },
      image: {
        class: ImageTool,
        config: {
          uploader: {
            async uploadByFile(file) {
              let upload = await UploadFile(getContext(route), file);

              return {
                success: 1,
                file: {
                  url: upload.url
                }
              }
            }
          }
        }
      }
    }
  });
});

async function askUserForChange() {
  showChangeLogPanel.value = true;
}

async function save() {
  if (!changeComment.value) {
    changeComment.value = "Updated the panel";
  }
  
  showChangeLogPanel.value = false;
  isLoading.value = true;

  const data = await editor.save();
  panel.value.schema = JSON.stringify(data);
  panel.value.changeComment = changeComment.value
  
  let response = await UpdatePanel(getContext(route), panel.value);

  isLoading.value = false;
}

function copyToken() {
  navigator.clipboard.writeText(token.value);
}
</script>

<template>
  <PanelLayout :organisation="organisation" :app="app" :panel="panel" v-if="organisation && app && panel">
    <div class="w-full">
      <div class="flex items-center justify-between space-y-2 bg-gray-100 p-4 rounded-md">
        <div class="flex items-center space-x-2">
          <Label for="name">Token</Label>
          <Input type="text" v-model="token" readonly/>
          <Button @click="copyToken" variant="ghost" size="sm">
            <Copy/>
          </Button>
        </div>
        <div class="flex items-center space-x-2">
          <Button @click="askUserForChange" :disabled="isLoading">
            <Loader v-if="isLoading" class="mr-2 h-4 w-4 animate-spin"/>
            <Save/>
            Save
          </Button>
        </div>
      </div>

      <div class="w-full outline-solid rounded-md mt-8 px-24 py-8">
        <div id="editorjs"></div>
      </div>
    </div>
  </PanelLayout>

  <!-- Modal background overlay -->
  <div id="comment-modal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" v-if="showChangeLogPanel">
    <!-- Modal container -->
    <div class="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
      <h2 class="text-xl font-bold mb-4">Change summary</h2>
      <p class="text-gray-600 mb-4">
        Optional
      </p>

      <textarea
          id="comment-text"
          rows="5"
          class="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Updated the panel"
          v-model="changeComment"
      ></textarea>

      <div class="flex justify-end gap-4 mt-6">
        <button
            class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
            @click="showChangeLogPanel = false"
        >
          Cancel
        </button>
        <button
            class="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            @click="save"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<style>
.codex-editor {
  h1 {
    font-size: 48px;
  }

  h2 {
    font-size: 32px;
  }

  h3 {
    font-size: 24px;
  }

  h4 {
    font-size: 20px;
  }

  h5 {
    font-size: 16px;
  }

  h6 {
    font-size: 14px;
  }
}
</style>